import { useState } from 'react';
import { t } from '../../i18n';
import { formatCHF, formatDateTime, parseDecimalInput } from '@werkdelta/shared';
import { Feld, Fehler, useConfirm } from '../../components/ui';
import { api, ApiError } from '../../api';
import type { StepProps } from '../CasePage';

/**
 * Schritt 10: Abschluss — Freigabe durch Gutachter, unveränderlicher Snapshot,
 * PDF mit Prüfsumme und Version; Nachträge erzeugen neue Versionen.
 * Export bleibt lizenzunabhängig möglich (Sperre 15.10).
 */
export function Step10Abschluss({ me, caseId, data, reload, readonly }: StepProps) {
  const [rounding, setRounding] = useState<100 | 1000>(100);
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const { confirm, dialog } = useConfirm();

  const versions = data.reports?.[0]?.versions ?? [];
  const canFinalize =
    !readonly && (me.role === 'GUTACHTER' || me.role === 'ADMIN') && !me.organization.licenseExpired;

  async function finalize() {
    if (
      !(await confirm(
        t('step10.finalizeConfirm', { name: me.displayName }),
      ))
    ) {
      return;
    }
    setError(null);
    setBusy(true);
    try {
      await api.post(`/api/v1/cases/${caseId}/reports/finalize`, {
        displayRoundCHF: rounding,
        remarks: remarks || undefined,
      });
      setRemarks('');
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'INTERNAL');
    } finally {
      setBusy(false);
    }
  }

  async function draft() {
    setError(null);
    setBusy(true);
    try {
      const r = await fetch(`/api/v1/cases/${caseId}/reports/draft`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'x-werkdelta-csrf': '1' },
        body: JSON.stringify({ displayRoundCHF: rounding, remarks: remarks || undefined }),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { code?: string };
        setError(j.code ?? 'INTERNAL');
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'werkdelta-entwurf.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('INTERNAL');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      {dialog}
      <section className="karte">
        <h2>{t('step10.title')}</h2>
        <p className="erklaerung">{t('step10.intro')}</p>
        <p className="hinweis">{t('report.mandatory')}</p>

        <ErgebnisRegister data={data} />
      </section>

      <ZusammenfuehrungKarte
        me={me}
        caseId={caseId}
        data={data}
        reload={reload}
        readonly={readonly}
      />

      {canFinalize && (
        <section className="karte">
            <h3>{t('step10.finalizeTitle')}</h3>
            <div className="feld-zeile">
              <Feld label={t('step9.displayRounding')} hint={t('step10.roundingHint')}>
                <select
                  value={rounding}
                  onChange={(e) => setRounding(Number(e.target.value) as 100 | 1000)}
                >
                  <option value={100}>{t('step9.round100')}</option>
                  <option value={1000}>{t('step9.round1000')}</option>
                </select>
              </Feld>
              <Feld label={t('step10.remarks')}>
                <input value={remarks} onChange={(e) => setRemarks(e.target.value)} />
              </Feld>
            </div>
            <Fehler code={error} />
            <div className="aktionen">
              <button type="button" className="knopf-primaer" onClick={finalize} disabled={busy}>
                {busy ? t('step10.finalizing') : t('step10.finalizeAction')}
              </button>
              <button type="button" className="knopf-sekundaer" onClick={draft} disabled={busy}>
                {t('step10.draftAction')}
              </button>
            </div>
            <p className="meta">{t('step10.draftHint')}</p>
        </section>
      )}

      <section className="karte">
        <h3>{t('step10.versionsTitle')}</h3>
        {versions.length === 0 ? (
          <div className="leer-hinweis">{t('step10.versionsEmpty')}</div>
        ) : (
          <div className="tabelle-scroll">
          <table className="daten">
            <thead>
              <tr>
                <th>{t('common.version')}</th>
                <th>{t('step10.createdAt')}</th>
                <th>{t('step10.approvedBy')}</th>
                <th>{t('step10.checksum')}</th>
                <th>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v: any) => (
                <tr key={v.id}>
                  <td className="zahl">{v.versionNumber}</td>
                  <td>{formatDateTime(v.createdAt)}</td>
                  <td>{v.approvedByName}</td>
                  <td>
                    <code>{v.snapshotSha256.slice(0, 16)}</code>
                  </td>
                  <td>
                    <a
                      className="knopf knopf-sekundaer knopf-klein"
                      href={`/api/v1/cases/${caseId}/reports/versions/${v.id}/pdf`}
                    >
                      {t('step10.downloadPdf')}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </section>

      <section className="karte">
        <h3>{t('step10.exportTitle')}</h3>
        <p className="erklaerung">{t('step10.exportIntro')}</p>
        <a className="knopf knopf-primaer" href={`/api/v1/cases/${caseId}/export.zip`}>
          {t('step10.exportAction')}
        </a>
      </section>
    </>
  );
}

/**
 * Manuelle Zusammenführung (Auftrag 3.3): Begründung + Überlappungsprüfung
 * je Paar sind Pflicht; den Betrag legt der Gutachter fest — nie die Software.
 */
function ZusammenfuehrungKarte({
  me,
  caseId,
  data,
  reload,
  readonly,
}: Pick<StepProps, 'me' | 'caseId' | 'data' | 'reload' | 'readonly'>) {
  const [selected, setSelected] = useState<string[]>([]);
  const [justification, setJustification] = useState('');
  const [amount, setAmount] = useState('');
  const [pairs, setPairs] = useState<Record<string, { same: string; comment: string }>>({});
  const [error, setError] = useState<string | null>(null);

  const canMerge =
    !readonly && (me.role === 'GUTACHTER' || me.role === 'ADMIN') && data.claimScenarios.length >= 2;
  if (!canMerge && (data.scenarioMerges ?? []).length === 0) return null;

  const pairList: [string, string][] = [];
  for (let i = 0; i < selected.length; i++) {
    for (let j = i + 1; j < selected.length; j++) {
      pairList.push([selected[i] as string, selected[j] as string]);
    }
  }
  const scenarioTitle = (id: string) =>
    data.claimScenarios.find((s: any) => s.id === id)?.title ?? '?';

  async function submit() {
    setError(null);
    const parsedAmount = amount ? parseDecimalInput(amount) : null;
    if (!parsedAmount) {
      setError('VALIDATION');
      return;
    }
    try {
      await api.post(`/api/v1/cases/${caseId}/scenarios/merge`, {
        scenarioIds: selected,
        justification,
        amountCombined: parsedAmount,
        overlapMatrix: pairList.map(([a, b]) => ({
          scenarioAId: a,
          scenarioBId: b,
          sameValueComponent: pairs[`${a}|${b}`]?.same === 'ja',
          comment: pairs[`${a}|${b}`]?.comment || undefined,
        })),
      });
      setSelected([]);
      setJustification('');
      setAmount('');
      setPairs({});
      reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.code : 'INTERNAL');
    }
  }

  return (
    <section className="karte">
      <h3>{t('step10.mergeTitle')}</h3>
      <p className="erklaerung">{t('step10.mergeIntro')}</p>
      {(data.scenarioMerges ?? []).map((m: any) => (
        <div className="eintrag" key={m.id}>
          <div className="eintrag-kopf">
            <strong>
              {m.links
                .map((l: any) => scenarioTitle(l.scenarioId))
                .join(' + ')}
            </strong>
            <span className="badge tuerkis">{formatCHF(String(m.amountCombined))}</span>
          </div>
          <p className="meta">{m.justification}</p>
        </div>
      ))}
      {canMerge && (
        <>
          <fieldset>
            <legend>{t('step10.mergeSelect')}</legend>
            {data.claimScenarios.map((s: any) => (
              <div className="kontrollzeile" key={s.id}>
                <input
                  id={`merge-${s.id}`}
                  type="checkbox"
                  checked={selected.includes(s.id)}
                  onChange={(e) =>
                    setSelected(
                      e.target.checked ? [...selected, s.id] : selected.filter((x) => x !== s.id),
                    )
                  }
                />
                <label htmlFor={`merge-${s.id}`}>
                  {s.title} ({t(`scenario.type.${s.type}`)})
                </label>
              </div>
            ))}
          </fieldset>
          {pairList.length > 0 && (
            <fieldset>
              <legend>{t('step10.mergeOverlapLegend')}</legend>
              {pairList.map(([a, b]) => {
                const key = `${a}|${b}`;
                return (
                  <div className="feld-zeile" key={key}>
                    <Feld
                      label={t('step10.mergeOverlapQuestion', {
                        a: scenarioTitle(a),
                        b: scenarioTitle(b),
                      })}
                      required
                    >
                      <select
                        value={pairs[key]?.same ?? ''}
                        onChange={(e) =>
                          setPairs({
                            ...pairs,
                            [key]: { same: e.target.value, comment: pairs[key]?.comment ?? '' },
                          })
                        }
                        required
                      >
                        <option value="">—</option>
                        <option value="ja">{t('common.yes')}</option>
                        <option value="nein">{t('common.no')}</option>
                      </select>
                    </Feld>
                    <Feld label={t('step10.mergeOverlapComment')}>
                      <input
                        value={pairs[key]?.comment ?? ''}
                        onChange={(e) =>
                          setPairs({
                            ...pairs,
                            [key]: { same: pairs[key]?.same ?? '', comment: e.target.value },
                          })
                        }
                      />
                    </Feld>
                  </div>
                );
              })}
            </fieldset>
          )}
          {selected.length >= 2 && (
            <>
              <Feld label={t('common.justification')} required>
                <textarea
                  value={justification}
                  onChange={(e) => setJustification(e.target.value)}
                  required
                />
              </Feld>
              <Feld
                label={t('step10.mergeAmount')}
                required
                hint={t('step10.mergeAmountHint')}
              >
                <input value={amount} onChange={(e) => setAmount(e.target.value)} />
              </Feld>
              <Fehler code={error} />
              <div className="aktionen">
                <button
                  type="button"
                  className="knopf-primaer"
                  onClick={submit}
                  disabled={
                    !justification.trim() ||
                    !amount.trim() ||
                    pairList.some(([a, b]) => !pairs[`${a}|${b}`]?.same)
                  }
                >
                  {t('step10.mergeAction')}
                </button>
              </div>
            </>
          )}
        </>
      )}
    </section>
  );
}

/** Ergebnisregister: Szenarien getrennt, ohne Summe (Sperre 15.2). */
function ErgebnisRegister({ data }: { data: any }) {
  const rows = data.claimScenarios.map((s: any) => {
    const vm = data.valuationModels.find((m: any) => m.claimScenarioId === s.id);
    const lastCalc = vm?.calculations?.[0];
    return { scenario: s, vm, lastCalc };
  });
  return (
    <>
      <h3>{t('step10.registerTitle')}</h3>
      <p className="hinweis">{t('scenario.noAutoSum')}</p>
      {rows.length === 0 ? (
        <div className="leer-hinweis">{t('step7.scenariosEmpty')}</div>
      ) : (
        <div className="tabelle-scroll">
        <table className="daten">
          <thead>
            <tr>
              <th>{t('step7.scenarioTitle')}</th>
              <th>{t('step7.scenarioType')}</th>
              <th>{t('common.status')}</th>
              <th className="zahl">{t('step10.amount')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ scenario, lastCalc }: any) => (
              <tr key={scenario.id}>
                <td>{scenario.title}</td>
                <td>{t(`scenario.type.${scenario.type}`)}</td>
                <td>
                  <span className="badge">{t(`scenario.status.${scenario.status}`)}</span>
                </td>
                <td className="zahl">
                  {scenario.type === 'MERKANTILER_MINDERWERT'
                    ? t('step10.noCalcPath')
                    : scenario.amountManual
                      ? formatCHF(scenario.amountManual)
                      : lastCalc
                        ? t('step10.seeCalculation')
                        : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </>
  );
}
